/*
 * The HTTP core. Every request in the app goes through `request()` — there are
 * no bare fetch calls anywhere else, which is what makes auth, refresh,
 * timeouts and error shape uniform instead of per-caller.
 */

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

/* Storage keys are shared with the existing kontact.id web app on purpose:
 * both surfaces are the same product and a user signed in on one should not be
 * bounced by the other. These names are load-bearing — see the x92 README. */
const TOKEN_KEY = "x92_token";
const REFRESH_KEY = "x92_refresh";

export const tokenStore = {
  get: () => safeGet(TOKEN_KEY),
  getRefresh: () => safeGet(REFRESH_KEY),
  set: (token: string, refresh?: string) => {
    safeSet(TOKEN_KEY, token);
    if (refresh) safeSet(REFRESH_KEY, refresh);
  },
  clear: () => {
    safeRemove(TOKEN_KEY);
    safeRemove(REFRESH_KEY);
  },
};

function safeGet(k: string) { try { return localStorage.getItem(k); } catch { return null; } }
function safeSet(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* private mode */ } }
function safeRemove(k: string) { try { localStorage.removeItem(k); } catch { /* private mode */ } }

/* ── Error taxonomy ──────────────────────────────────────────────────────
 * Components must be able to branch on *why* a call failed without parsing
 * strings. `kind` is the only thing the UI should ever switch on.
 */
export type ApiErrorKind =
  | "network"      /* never reached the server — offline, DNS, CORS         */
  | "timeout"
  | "unauthorized" /* 401 after refresh already failed                      */
  | "forbidden"    /* 403 — authenticated but not allowed                   */
  | "notfound"
  | "server"       /* 5xx                                                   */
  | "client"       /* other 4xx                                             */
  | "contract";    /* 2xx whose body did not match the declared schema      */

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly path: string;
  readonly detail?: unknown;

  constructor(kind: ApiErrorKind, message: string, path: string, status = 0, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.path = path;
    this.detail = detail;
  }

  /** Retrying a contract or auth failure just burns requests. */
  get retryable() {
    return this.kind === "network" || this.kind === "timeout" || this.kind === "server";
  }
}

/* ── Refresh, serialised ─────────────────────────────────────────────────
 * The dashboard fires several requests on mount. Without this mutex an expired
 * token produces N concurrent refreshes, N-1 of which race and lose — and
 * because each rotates the refresh token server-side, the losers can invalidate
 * the winner and log the user out mid-session.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = tokenStore.getRefresh();
    if (!refresh) return null;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string; refreshToken?: string };
      if (!data.token) return null;
      tokenStore.set(data.token, data.refreshToken);
      return data.token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Broadcast so the auth provider can react without importing this module's
 *  internals or polling storage. */
function emitSignedOut() {
  window.dispatchEvent(new CustomEvent("kontact:signed-out"));
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Milliseconds before the request is aborted. */
  timeout?: number;
  /** Skip the Authorization header (login, health). */
  anonymous?: boolean;
}

const DEFAULT_TIMEOUT = 15_000;

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, timeout = DEFAULT_TIMEOUT, anonymous } = opts;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const send = async (token: string | null): Promise<Response> => {
    /* One controller per attempt: an AbortSignal is single-use, so reusing it
     * across the retry-after-refresh would abort the retry immediately. */
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(new DOMException("timeout", "TimeoutError")), timeout);
    const onOuterAbort = () => ctrl.abort(signal?.reason);
    signal?.addEventListener("abort", onOuterAbort, { once: true });

    try {
      return await fetch(url.toString(), {
        method,
        signal: ctrl.signal,
        headers: {
          Accept: "application/json",
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } finally {
      window.clearTimeout(timer);
      signal?.removeEventListener("abort", onOuterAbort);
    }
  };

  let res: Response;
  try {
    res = await send(anonymous ? null : tokenStore.get());
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new ApiError("timeout", `Request timed out after ${timeout}ms`, path);
    }
    if (signal?.aborted) throw err; // caller cancelled; not an API failure
    throw new ApiError("network", "Could not reach the API", path, 0, err);
  }

  /* 401 → refresh once, then replay. A second 401 means the session is gone. */
  if (res.status === 401 && !anonymous) {
    const fresh = await refreshToken();
    if (fresh) {
      try {
        res = await send(fresh);
      } catch (err) {
        throw new ApiError("network", "Could not reach the API", path, 0, err);
      }
    }
    if (res.status === 401) {
      tokenStore.clear();
      emitSignedOut();
      throw new ApiError("unauthorized", "Session expired", path, 401);
    }
  }

  if (!res.ok) {
    const detail = await res.json().catch(() => undefined);
    /* This API is inconsistent about which key carries the human-readable
     * reason: Fastify's own errors use `message`, hand-thrown ones in the route
     * handlers use `error`. Reading only `message` is why a perfectly explicit
     * server error ("Passkey subsystem unavailable: JWT_SECRET is not
     * provisioned...") surfaced in the UI as the bare string "500". */
    const fromBody =
      detail && typeof detail === "object"
        ? ["message", "error", "detail"]
            .map((k) => (k in detail ? (detail as Record<string, unknown>)[k] : undefined))
            .find((v) => typeof v === "string" && v.length > 0)
        : undefined;
    const message = (fromBody as string | undefined) ?? `${res.status} ${res.statusText}`.trim();
    throw new ApiError(statusToKind(res.status), message, path, res.status, detail);
  }

  if (res.status === 204) return undefined as T;

  try {
    return (await res.json()) as T;
  } catch (err) {
    throw new ApiError("contract", "Response was not valid JSON", path, res.status, err);
  }
}

function statusToKind(status: number): ApiErrorKind {
  if (status === 403) return "forbidden";
  if (status === 404) return "notfound";
  if (status >= 500) return "server";
  return "client";
}
